import React, { useState, useEffect } from 'react';
import { FolderTree, Plus, Trash2, Pencil, Loader2, X, Users, BookOpen, ChevronRight, ChevronDown } from 'lucide-react';
import { API } from '../../App';

/**
 * CategoryManager — Manage persona groups and playbook categories at enterprise or team scope.
 *
 * Props:
 *   wsId  — workspace ID
 *   scope — 'enterprise' | 'team'
 */
export default function CategoryManager({ wsId, scope = 'enterprise' }) {
  const [activeTab, setActiveTab] = useState('playbook-categories');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category: '', icon: '', display_order: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const isPlaybook = activeTab === 'playbook-categories';
  const baseUrl = scope === 'enterprise'
    ? `/api/enterprise/${wsId}/${activeTab}`
    : `/api/workspaces/${wsId}/${activeTab}`;
  const itemLabel = isPlaybook ? 'Playbook Category' : 'Persona Group';
  const responseKey = isPlaybook ? 'categories' : 'groups';

  const load = () => {
    if (!wsId) return;
    setLoading(true);
    API.get(baseUrl)
      .then(r => setItems(r.data[responseKey] || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [wsId, activeTab, scope]);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await API.put(`${baseUrl}/${editing}`, form);
      } else {
        await API.post(baseUrl, form);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', description: '', category: '', icon: '', display_order: 0 });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed');
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${itemLabel.toLowerCase()}?`)) return;
    try {
      await API.delete(`${baseUrl}/${id}`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({
      name: item.name || '',
      description: item.description || '',
      category: item.category || '',
      icon: item.icon || '',
      display_order: item.display_order || 0,
    });
    setShowForm(true);
  };

  const scopeLabel = scope === 'enterprise' ? 'Enterprise' : 'Team';
  const scopeBadgeColor = scope === 'enterprise' ? 'bg-purple-900/30 text-purple-400' : 'bg-blue-900/30 text-blue-400';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FolderTree className="w-5 h-5 text-[#FF4D00]" />
          <h2 className="text-lg font-bold font-['Barlow_Condensed']">{scopeLabel} Categories</h2>
          <span className={`text-xs px-2 py-0.5 rounded ${scopeBadgeColor}`}>{scope}</span>
        </div>
      </div>

      {/* Sub-tabs: Playbook Categories vs Persona Groups */}
      <div className="flex gap-2">
        <button
          onClick={() => { setActiveTab('playbook-categories'); setShowForm(false); setEditing(null); setExpandedId(null); }}
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono min-h-[44px] ${
            isPlaybook ? 'bg-[#FF4D00] text-black' : 'bg-[#0F0F0F] border border-[#2A2A2A] text-[#A0A0A0]'
          }`}
        >
          <BookOpen className="w-3.5 h-3.5" /> Playbook Categories
        </button>
        <button
          onClick={() => { setActiveTab('persona-groups'); setShowForm(false); setEditing(null); setExpandedId(null); }}
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono min-h-[44px] ${
            !isPlaybook ? 'bg-[#FF4D00] text-black' : 'bg-[#0F0F0F] border border-[#2A2A2A] text-[#A0A0A0]'
          }`}
        >
          <Users className="w-3.5 h-3.5" /> Persona Groups
        </button>
        <div className="flex-1" />
        <button
          onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', description: '', category: '', icon: '', display_order: 0 }); }}
          className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium flex items-center gap-2 min-h-[44px]"
        >
          <Plus className="w-4 h-4" /> New {itemLabel}
        </button>
      </div>

      {/* Create/Edit form */}
      {showForm && (
        <div className="p-4 bg-[#0F0F0F] border border-[#FF4D00]/30 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold">{editing ? 'Edit' : 'New'} {itemLabel}</h3>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Name" className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
          <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
            placeholder="Category tag" className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
          <input value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })}
            placeholder="Icon (emoji or class)" className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="Description" rows={2} className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00] resize-none" />
          <input type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
            placeholder="Display order" className="w-32 px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
          {error && <p className="text-[#EF4444] text-xs">{error}</p>}
          <button onClick={save} disabled={saving || !form.name.trim()}
            className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50 min-h-[44px]">
            {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center gap-2 text-[#A0A0A0] py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading {activeTab.replace('-', ' ')}...
        </div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div className="text-center py-8 text-[#A0A0A0]">
          <FolderTree className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No {scopeLabel.toLowerCase()} {activeTab.replace('-', ' ')} yet</p>
        </div>
      )}

      {/* List */}
      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map(item => {
            const isExpanded = expandedId === item.id;
            const members = item.members || [];
            const canEdit = item.scope === scope;
            return (
              <div key={item.id} className="bg-[#0F0F0F] border border-[#2A2A2A]" data-testid={`category-row-${item.id}`}>
                <div className="flex items-center gap-3 p-3">
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : item.id)}
                    className="flex-1 min-w-0 text-left flex items-center gap-2 min-h-[44px]"
                    data-testid={`category-expand-${item.id}`}
                    aria-expanded={isExpanded}
                  >
                    {isExpanded
                      ? <ChevronDown className="w-4 h-4 text-[#A0A0A0] shrink-0" />
                      : <ChevronRight className="w-4 h-4 text-[#A0A0A0] shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {item.icon && <span className="text-sm">{item.icon}</span>}
                        <span className="text-sm font-medium truncate">{item.name}</span>
                        {item.category && <span className="text-xs px-1.5 py-0.5 bg-[#2A2A2A] text-[#A0A0A0]">{item.category}</span>}
                        <span className={`text-xs px-1.5 py-0.5 rounded ${scopeBadgeColor}`}>{item.scope || scope}</span>
                        {item.member_count != null && (
                          <span className="text-xs text-[#A0A0A0]">{item.member_count} {isPlaybook ? 'playbooks' : 'personas'}</span>
                        )}
                      </div>
                      {item.description && <p className="text-xs text-[#A0A0A0] truncate mt-0.5">{item.description}</p>}
                    </div>
                  </button>
                  {canEdit && (
                    <>
                      <button onClick={() => startEdit(item)} className="p-2 text-[#A0A0A0] hover:text-[#FF4D00] min-w-[44px] min-h-[44px] flex items-center justify-center">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(item.id)} className="p-2 text-[#A0A0A0] hover:text-[#EF4444] min-w-[44px] min-h-[44px] flex items-center justify-center">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
                {isExpanded && (
                  <div className="border-t border-[#2A2A2A] p-3 bg-[#0A0A0A]" data-testid={`category-members-${item.id}`}>
                    {members.length === 0 ? (
                      <p className="text-xs text-[#A0A0A0] py-2">
                        No {isPlaybook ? 'playbooks' : 'personas'} in this {isPlaybook ? 'category' : 'group'} yet.
                      </p>
                    ) : (
                      <ul className="space-y-1">
                        {members.map(m => (
                          <li key={m.id} className="flex items-center gap-2 text-xs text-[#F5F1E8] py-1">
                            {isPlaybook ? <BookOpen className="w-3 h-3 text-[#A0A0A0] shrink-0" /> : <Users className="w-3 h-3 text-[#A0A0A0] shrink-0" />}
                            <span className="truncate">{m.name || m.slug || m.id}</span>
                            {m.category && <span className="text-[10px] px-1 bg-[#2A2A2A] text-[#A0A0A0]">{m.category}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                    {!canEdit && (
                      <p className="text-[10px] font-mono text-[#A0A0A0] mt-2">
                        Read-only — {item.scope} scope
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
